import type { V2CreateAdminPropertyInput } from '@idc-forms/schemas/api-v2/admin-properties'
import { CircleOff, CirclePlay, FolderOpen, Images, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { DocumentPortfolioTab } from '@/features/documents/components/document-portfolio-tab'
import { GalleryTabWithCopy } from '@/features/documents/components/gallery-tab-with-copy'
import {
  INVENTORY_STATUS_COLORS,
  INVENTORY_STATUS_LABELS,
  type InventoryStatus
} from '@/features/inventory/models/inventory'
import { Permission } from '@/features/roles/consts/permissions'
import { formatTypePropertyLabel } from '@/features/type-properties/models/type-property-label'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { ScrollFade } from '@/shared/components/scroll-fade/scroll-fade'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import {
  useDeletePropertyV2Mutation,
  useUpdatePropertyV2Mutation
} from '../hooks/use-property-v2-mutations'
import type { PropertyV2AdminRow } from '../models/property-admin-v2'
import { PropertyV2Form } from './property-v2-form'

/** Alias local: fila estándar de los paneles de detalle (familia compartida). */
const FieldRow = DetailFieldRow

const UNIT_TYPE_LABELS: Record<string, string> = {
  APT: 'Apartamento',
  V: 'Villa',
  C: 'Casa'
}

const STRUCTURE_TYPE_LABELS: Record<string, string> = {
  T: 'Torre',
  V: 'Villa'
}

/** Money (GTQ) → quetzales legibles para el detalle; `null` si no hay dato. */
function formatIusi(money: PropertyV2AdminRow['iusiAmount']): string | null {
  if (!money) return null
  return `Q ${(money.amountMinor / 100).toLocaleString('es-GT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

type PropertyV2DetailPanelProps = {
  property: PropertyV2AdminRow
}

/**
 * Panel de detalle del inmueble contra la API v2. Espeja el panel v1 —mismo
 * `PropertyV2Form`, mismo badge de estado, mismos acordeones— y sólo cambia el
 * transporte: direcciona por `prp_`, el proyecto llega como `{ id: prj_, name,
 * code }`, el subtipo como `{ id: typ_, code, name }` y el IUSI como Money (GTQ).
 */
export function PropertyV2DetailPanel({ property }: PropertyV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdatePropertyV2Mutation()
  const deleteMutation = useDeletePropertyV2Mutation()
  const { has } = usePermissions()

  const statusLabel = INVENTORY_STATUS_LABELS[property.availabilityStatus as InventoryStatus]

  const toggleActive = () => {
    updateMutation.mutate({ id: property.id, body: { active: !property.active } })
  }

  if (mode === 'edit') {
    return (
      <PropertyV2Form
        defaultValues={{
          // El SDK tipa los ids como string plano; el form usa los branded del
          // schema (`prj_`/`typ_`). Mismo dato en runtime.
          name: property.name,
          code: property.code,
          project: property.project?.id as V2CreateAdminPropertyInput['project'] | undefined,
          typeProperty: property.typeProperty?.id as
            | V2CreateAdminPropertyInput['typeProperty']
            | undefined,
          unitType: property.unitType ?? undefined,
          tipo: property.tipo ?? undefined,
          structureType: property.structureType ?? undefined,
          structureNumber: property.structureNumber ?? undefined,
          identifier: property.identifier ?? undefined,
          constructionM2: property.constructionM2 ?? undefined,
          terrainM2: property.terrainM2 ?? undefined,
          terrainV2: property.terrainV2 ?? undefined,
          terraceM2: property.terraceM2 ?? undefined,
          parkingM2: property.parkingM2 ?? undefined,
          property: property.property ?? undefined,
          folio: property.folio ?? undefined,
          book: property.book ?? undefined,
          waterMeter: property.waterMeter ?? undefined,
          electricityMeter: property.electricityMeter ?? undefined,
          iusiAmount: property.iusiAmount ?? undefined,
          telephone: property.telephone ?? undefined,
          active: property.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(
            { id: property.id, body: values },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold leading-tight">{property.name}</h3>
            {statusLabel && (
              <span
                className={cn(
                  'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  INVENTORY_STATUS_COLORS[property.availabilityStatus as InventoryStatus]
                )}
              >
                {statusLabel}
              </span>
            )}
          </div>
          <FieldRow label="Código" value={property.code} copyValue={property.code} />
          <FieldRow label="Proyecto" value={property.project?.name ?? null} />
          <FieldRow
            label="Subtipo"
            value={formatTypePropertyLabel(
              property.typeProperty?.code,
              property.typeProperty?.name
            )}
          />
          <FieldRow
            label="Tipo de unidad"
            value={property.unitType ? UNIT_TYPE_LABELS[property.unitType] : null}
          />
          <FieldRow label="Activo" value={property.active ? 'Sí' : 'No'} />
          <FieldRow label="En negociación" value={property.inNegotiation ? 'Sí' : 'No'} />
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateProperties)
              ? [
                  {
                    label: 'Editar inmueble',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: property.active ? 'Desactivar inmueble' : 'Activar inmueble',
                    icon: property.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteProperties)
              ? [
                  {
                    label: 'Eliminar inmueble',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        {/*
          `ScrollFade` alrededor de la `TabsList`, como ya lo tiene el subtipo. No es adorno:
          estas pestañas viven en un panel que se arrastra hasta 320 px, y ahí tres pestañas no
          entran. Sin esto la última se RECORTA sin ninguna señal de que hay más — el usuario ve
          dos pestañas y concluye que son dos. El degradado del borde dice que sigue, y el
          `w-max min-w-full` deja que la lista crezca en vez de comprimir los rótulos.
        */}
        <ScrollFade className="w-full" scrollClassName="overflow-y-hidden">
          <TabsList className="flex h-auto w-max min-w-full justify-normal rounded-none">
            <TabsTrigger className="flex gap-1" value="more-info">
              <Info className="size-4" /> Más información
            </TabsTrigger>
            <TabsTrigger className="flex gap-1" value="portfolio">
              <FolderOpen className="size-4" /> Cartapacio
            </TabsTrigger>

            {/* La galería va aparte del cartapacio porque el trabajo es otro: ahí se
                clasifica una lista de papeles, acá se decide qué se ve y en qué orden. */}
            <TabsTrigger className="flex gap-1" value="gallery">
              <Images className="size-4" /> Galería
            </TabsTrigger>
          </TabsList>
        </ScrollFade>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['identification']}>
            <AccordionItem value="identification">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Identificación
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <FieldRow label="Tipo" value={property.tipo} />
                  <FieldRow
                    label="Estructura"
                    value={
                      property.structureType ? STRUCTURE_TYPE_LABELS[property.structureType] : null
                    }
                  />
                  <FieldRow label="No. de estructura" value={property.structureNumber} />
                  <FieldRow label="Identificador" value={property.identifier} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="construction-data">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Datos de construcción
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <FieldRow label="Construcción (m²)" value={property.constructionM2} />
                  <FieldRow label="Terreno (m²)" value={property.terrainM2} />
                  <FieldRow label="Terreno (v²)" value={property.terrainV2} />
                  <FieldRow label="Terraza (m²)" value={property.terraceM2} />
                  <FieldRow label="Parqueo (m²)" value={property.parkingM2} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="additional-data">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Datos adicionales
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <FieldRow label="Finca" value={property.property} />
                  <FieldRow label="Folio" value={property.folio} />
                  <FieldRow label="Libro" value={property.book} />
                  <FieldRow label="Monto IUSI" value={formatIusi(property.iusiAmount)} />
                  <FieldRow label="Contador de agua" value={property.waterMeter} />
                  <FieldRow label="Contador de luz" value={property.electricityMeter} />
                  <FieldRow label="Teléfono" value={property.telephone} />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
        <TabsContent value="portfolio">
          <DocumentPortfolioTab ownerType="property" ownerId={property.id} />
        </TabsContent>
        <TabsContent value="gallery">
          <GalleryTabWithCopy
            ownerType="property"
            ownerId={property.id}
            ownerLabel={property.code}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar inmueble</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{property.name}” ({property.code}). Esta acción no se puede deshacer
              desde la interfaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(property.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
